import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';

import CardCarousel from 'components/CardCarousel';
import CategoryCarousel from 'components/CategoryCarousel';
import EmailSignupModule from 'components/EmailSignupModule';
import { Grid, Cell } from 'components/Grid';
import HomepageHero from 'components/HomepageHero';
import SecondaryPromoHero from 'components/HomepageV2SecondaryPromoHero';
import { MINI_NAV_POSITIONS } from 'components/MiniNavigation/constants';
import StoryModule from 'components/StoryModule';
import VideoModal from 'components/VideoModal';
import { OVERLAY_POSITIONS, PAGE_SECTIONS } from 'constants/homepage';
import CardCarouselModel from 'models/CardCarousel';
import CategoryCarouselModel from 'models/CategoryCarousel';
import HeroModel from 'models/Hero';
import PageSectionEmailSignupModel from 'models/PageSectionEmailSignupModule';
import PageSectionStoryModuleModel from 'models/PageSectionStoryModule';
import SecondaryPromoHeroModel from 'models/SecondaryPromoHero';

const HomepageV2 = ({
  closeModal,
  homepageData,
  isModalOpen,
  openModal,
  signUpForNewsletter,
  trackModuleEvent,
}) => {
  const [modalVideoUrl, setModalVideoUrl] = useState('');

  const { hero: heroV3, pageSections: _pageSections } = homepageData;
  const hero = new HeroModel(heroV3);

  const pageSections = map(_pageSections, data => {
    switch (get(data, '_contentType')) {
      case PAGE_SECTIONS.SECONDARY_PROMO_HERO:
        return new SecondaryPromoHeroModel(data);
      case PAGE_SECTIONS.CARD_CAROUSEL:
        return new CardCarouselModel(data);
      case PAGE_SECTIONS.CATEGORY_CAROUSEL:
        return new CategoryCarouselModel(data);
      case PAGE_SECTIONS.EMAIL_SIGNUP_MODULE:
        return new PageSectionEmailSignupModel(data);
      case PAGE_SECTIONS.STORY_MODULE:
        return new PageSectionStoryModuleModel(data);
      default:
        return null;
    }
  });

  const openVideoModal = useCallback(
    url => {
      setModalVideoUrl(url);
      openModal();
    },
    [openModal]
  );

  const handleEmailSubmit = useCallback(
    async (email, acceptsMarketing) => {
      signUpForNewsletter({
        emailAddress: email,
        origination: 'homepageSignupModule',
        acceptsMarketing,
      });
    },
    [signUpForNewsletter]
  );

  const renderPageSection = useCallback(
    (data, index) => {
      if (!data) return null;

      switch (data.type) {
        case PAGE_SECTIONS.SECONDARY_PROMO_HERO:
          return (
            <SecondaryPromoHero
              data={data}
              homepagePosition={index}
              openVideoModal={openVideoModal}
              trackModuleEvent={trackModuleEvent}
              videoModalOpen={isModalOpen}
            />
          );
        case PAGE_SECTIONS.CARD_CAROUSEL:
          return (
            <CardCarousel
              data={data}
              homepagePosition={index}
              trackModuleEvent={trackModuleEvent}
            />
          );
        case PAGE_SECTIONS.CATEGORY_CAROUSEL:
          return (
            <CategoryCarousel
              data={data}
              homepagePosition={index}
              trackModuleEvent={trackModuleEvent}
            />
          );
        case PAGE_SECTIONS.EMAIL_SIGNUP_MODULE:
          return (
            <EmailSignupModule
              backgroundColor={data.backgroundColor}
              data={data}
              disclaimer={data.disclaimer}
              homepagePosition={index}
              marketingMessage={data.marketingMessage}
              onSubmit={handleEmailSubmit}
              text={data.text}
              trackModuleEvent={trackModuleEvent}
            />
          );
        case PAGE_SECTIONS.STORY_MODULE:
          return (
            <StoryModule
              heading={data.heading}
              internalName={data.internalName}
              mobileCardLayout={data.mobileCardLayout}
              modulePosition={index}
              posterDimensions={data.posterDimensions}
              storyModuleCards={data.storyModuleCards}
              trackModuleEvent={trackModuleEvent}
            />
          );
        default:
          /* istanbul ignore next */
          return null;
      }
    },
    [openVideoModal, trackModuleEvent, isModalOpen, handleEmailSubmit]
  );

  return (
    <div className="Homepage bg-white lg:pb-0">
      <VideoModal closeModal={closeModal} isOpen={isModalOpen} videoUrl={modalVideoUrl} />
      <HomepageHero
        closeModal={closeModal}
        hero={hero}
        isModalOpen={isModalOpen}
        miniNavigationPosition={hero.miniNavigationPosition}
        openVideoModal={openVideoModal}
        overlayPositionDesktop={hero.overlayPositionDesktop}
        overlayPositionMobile={hero.overlayPositionMobile}
      />
      <Grid>
        {map(pageSections, (pageSectionData, index) => (
          <Cell smallUp={{ columns: 12 }} key={index}>
            <div className="Homepage__page-section">
              {renderPageSection(pageSectionData, index + 1)}
            </div>
          </Cell>
        ))}
      </Grid>
    </div>
  );
};

HomepageV2.propTypes = {
  closeModal: PropTypes.func,
  homepageData: PropTypes.shape({
    hero: PropTypes.shape({
      _contentType: PropTypes.string,
      headline: PropTypes.string,
      subheadline: PropTypes.string,
      ctaLinks: PropTypes.arrayOf(PropTypes.shape({})),
      backgroundImage: PropTypes.shape({}),
      miniNavigationPosition: PropTypes.oneOf(Object.values(MINI_NAV_POSITIONS)),
      overlayPositionDesktop: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
      overlayPositionMobile: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
      video: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }).isRequired,
    pageSections: PropTypes.array,
  }),
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  modalSettings: PropTypes.shape({}),
  openModal: PropTypes.func,
  signUpForNewsletter: PropTypes.func,
  trackModuleEvent: PropTypes.func,
};

HomepageV2.defaultProps = {
  homepageData: {
    hero: {},
    pageSections: [],
  },
  isModalOpen: false,
};

export default HomepageV2;
